// Phase 1b: TanStack Query bindings for the GovCon knowledge graph. The
// orchestration RPCs live on zou's ProjectDataService (GetEntityGraph,
// UpsertEntityNode, UpsertEntityEdge, OverrideEntityEdge, MergeEntityNodes),
// each proxied through sabaody and Cerbos-gated server-side.
import { Code, ConnectError } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
const GRAPH_KEY = 'entity-graph';
const ORG_CHART_KEY = 'gov-org-chart';
const RESOLUTION_KEY = 'resolution-history';
/** Load the tenant entity graph (nodes + edges) for the cards. */ export function useEntityGraph(includeSuperseded) {
    return useQuery({
        queryKey: [
            GRAPH_KEY,
            includeSuperseded
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.getEntityGraph({
                includeSuperseded
            });
            return resp.graph ?? {
                nodes: [],
                edges: []
            };
        }
    });
}
function useGraphMutation(fn, failMsg) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    GRAPH_KEY
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    ORG_CHART_KEY
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    RESOLUTION_KEY
                ]
            });
        },
        onError: (err)=>{
            // Graph mutations (delete / merge / override / unmerge) are admin-or-grant
            // gated server-side, so a non-admin gets a Cerbos PermissionDenied. Surface
            // an actionable message instead of the raw error string.
            if (err instanceof ConnectError && err.code === Code.PermissionDenied) {
                toast.error("You don't have access to do this. Request it from your admin or team supervisor.");
                return;
            }
            toast.error(err instanceof Error ? err.message : failMsg);
        }
    });
}
export function useUpsertEntityNode() {
    return useGraphMutation((args)=>projectDataClient.upsertEntityNode({
            id: args.id ?? '',
            nodeType: args.nodeType,
            displayName: args.displayName,
            ryuguRef: args.ryuguRef ?? ''
        }), 'Failed to save entity');
}
/** Soft-delete a node; the data plane cascades to its incident edges in one txn. */ export function useDeleteEntityNode() {
    return useGraphMutation((args)=>projectDataClient.deleteEntityNode({
            id: args.id
        }), 'Failed to delete entity');
}
export function useUpsertEntityEdge() {
    return useGraphMutation((args)=>projectDataClient.upsertEntityEdge({
            fromNode: args.fromNode,
            toNode: args.toNode,
            edgeType: args.edgeType,
            provenance: args.provenance ?? 'user_intel',
            source: args.source,
            confidence: args.confidence
        }), 'Failed to save relationship');
}
/** Soft-delete a single edge. */ export function useDeleteEntityEdge() {
    return useGraphMutation((args)=>projectDataClient.deleteEntityEdge({
            id: args.id
        }), 'Failed to delete relationship');
}
export function useOverrideEntityEdge() {
    return useGraphMutation((args)=>projectDataClient.overrideEntityEdge({
            edgeId: args.edgeId,
            rationale: args.rationale
        }), 'Failed to pin canonical relationship');
}
export function useMergeEntityNodes() {
    return useGraphMutation((args)=>projectDataClient.mergeEntityNodes({
            primaryNodeId: args.primaryNodeId,
            secondaryNodeId: args.secondaryNodeId,
            rationale: args.rationale
        }), 'Failed to merge entities');
}
/** Load the typed government org chart rooted at a gov_org node (Phase 1b-3). */ export function useGovOrgChart(rootNodeId) {
    return useQuery({
        queryKey: [
            ORG_CHART_KEY,
            rootNodeId
        ],
        enabled: !!rootNodeId,
        queryFn: async ()=>{
            const resp = await projectDataClient.getGovOrgChart({
                rootNodeId: rootNodeId ?? '',
                maxDepth: 0
            });
            return {
                orgs: resp.orgs ?? [],
                people: resp.people ?? []
            };
        }
    });
}
/** Reverse a merge by its resolution-log id (split, Phase 1b-4). */ export function useUnmergeEntityNodes() {
    return useGraphMutation((args)=>projectDataClient.unmergeEntityNodes({
            resolutionLogId: args.resolutionLogId,
            rationale: args.rationale
        }), 'Failed to unmerge entities');
}
/**
 * Load the tenant entity-resolution history (merge / split / override / pin),
 * newest first, for the resolution-history browser (Phase 1b-4). Lets a user
 * unmerge an older merge, not just the one they just made.
 */ export function useResolutionHistory() {
    return useQuery({
        queryKey: [
            RESOLUTION_KEY
        ],
        queryFn: async ()=>{
            const resp = await projectDataClient.listEntityResolutionLog({
                limit: 50
            });
            return resp.entries ?? [];
        }
    });
}
